import { FC, ReactElement, useEffect } from 'react'
import { Provider, useDispatch } from 'react-redux'
import { ScriptProps } from 'next/script'
import { NextRouter } from 'next/dist/shared/lib/router/router'
import { CookiesProvider } from 'react-cookie'

import store from 'store/index'
import { checkUserLogin } from 'store/reducers/users'
import { Transition } from 'components/global/transition'

import { applyGoogleTranslateFix } from 'utils/apply-google-translate-fix'

import dynamic from 'next/dynamic'

import AccountLayout from './account/layout'

import 'keen-slider/keen-slider.min.css'

import '../styles/fonts.css'
import '../styles/globals.css'
import '../styles/calendar.css'
import '../styles/variables.scss'
import '../styles/phone-input.scss'
import ErrorBoundary from './error-boundary'

const Footer = dynamic(() => import('components/ui/footer'), { ssr: true })
const FloatingTrustpilotContainer = dynamic(
  () => import('components/global/truspilot-widget').then((mod) => mod.FloatingTrustpilotContainer),
  { ssr: true },
)

function UserProvider(props) {
  const dispatch = useDispatch()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      void dispatch(checkUserLogin())
    }
  }, [])

  return props.children
}

export default function App({
  Component,
  pageProps,
  router,
}: {
  Component: FC
  pageProps: ScriptProps
  router: NextRouter
}): ReactElement {
  const code = Array.isArray(router.query.coin) ? router.query.coin[0] : router.query.coin

  useEffect(() => {
    const run = async () => {
      const { getGeolocation } = await import('../store/fetchers/utils')
      const { switchToTelegram } = await import('../utils/go-to-chat')

      const location = await getGeolocation()
      if (location?.response?.toLowerCase() === 'ru') {
        switchToTelegram()
      }
    }

    run()

    applyGoogleTranslateFix()
  }, [])

  if (router.pathname.startsWith('/account')) {
    return (
      <ErrorBoundary>
        <Provider store={store}>
          <UserProvider>
            <AccountLayout>
              <Component {...pageProps} />
            </AccountLayout>
          </UserProvider>
        </Provider>
      </ErrorBoundary>
    )
  }

  return (
    <>
      <CookiesProvider>
        <Provider store={store}>
          <UserProvider>
            <Transition location={router.pathname} code={code}>
              <Component {...pageProps} />
            </Transition>
            <FloatingTrustpilotContainer />
          </UserProvider>
        </Provider>
        <Footer />
      </CookiesProvider>
    </>
  )
}
